"use client"

import { motion, AnimatePresence } from 'framer-motion'
import { useState, useEffect } from 'react'
import { createPortal } from 'react-dom'
import {
	X, ImageIcon, Film, Clock, Award, DollarSign, User, Tag, Info, Shield, Database,
	Layers, Settings, Maximize, Hash, Scale, Maximize2, FileType, Percent, Eye,
	Wand2, LayoutTemplate, Music, Chrome, ZoomIn, Copy, Download,
	Video,
	Share
} from 'lucide-react'
import Image from 'next/image'
import { parseMediaDetails } from './MediaDetailsUtil'
import { toast } from 'sonner'
import { useRouter } from 'next/navigation'
import { CREATE_SIMILAR_BUTTON, DOWNLOAD_BUTTON } from '@/lib/theme-config'
import { DownloadOptions, useDownloadOptions } from '../tools-2/DownloadOptions'
import { useIsMobile } from '@/hooks/use-mobile'

/**
 * 媒体详情弹窗组件
 * @param {Object} props - 组件属性
 * @param {Object} props.selectedItem - 选中的媒体项
 * @param {Function} props.onClose - 关闭弹窗回调
 * @param {String} props.source - 数据来源，可以是 'history' 或 'explore'
 * @param {Object} props.currentUser - 当前用户信息
 */
export function MediaDetailsModal({ selectedItem, onClose, source = 'history', currentUser = null }) {
	console.log({
		selectedItem,
		onClose,
		source,
		currentUser,
	});

	// Hooks 必须在组件顶部无条件调用
	const [zoomImage, setZoomImage] = useState(null)
	const [isMounted, setIsMounted] = useState(false)
	const router = useRouter()
	const downloadOptions = useDownloadOptions()
	const isMobile = useIsMobile()

	// 在客户端渲染完成后设置 mounted 状态
	useEffect(() => {
		setIsMounted(true)
	}, [])

	// 当模态框打开时禁止背景滚动
	useEffect(() => {
		if (selectedItem) {
			document.body.style.overflow = 'hidden'
		} else {
			document.body.style.overflow = 'unset'
		}
		return () => {
			document.body.style.overflow = 'unset'
		}
	}, [selectedItem])

	// 如果不是在客户端或没有选中项，不渲染任何内容
	if (!isMounted || !selectedItem) return null

	try {
		const details = parseMediaDetails(selectedItem, source, currentUser)

		if (!details) {
			toast.error('Failed to parse media details')
			return null
		}

		const {
			isImage,
			mediaUrl,
			prompt,
			timestamp,
			status,
			taskId,
			email,
			userId,
			model,
			isPaid,
			isFeatured,
			id,
			hasFullAccess,
			rawData,
			userFriendlyData
		} = details

		// 解析媒体URL - 适配所有 webhook 的 output 格式（图片和视频统一处理）
		const parseVideoUrls = () => {
			let originalUrl = null // 不带水印的URL
			let watermarkedUrl = null // 带水印的URL

			// 检查原始类型
			const mediaType = selectedItem.originalType || selectedItem.type

			if (selectedItem.output) {
				let outputData = null

				// 如果 output 是字符串，先解析为对象
				if (typeof selectedItem.output === 'string') {
					try {
						outputData = JSON.parse(selectedItem.output)
					} catch (e) {
						console.warn('Failed to parse output JSON:', e)
						// 如果解析失败，可能是直接的 URL 字符串
						originalUrl = selectedItem.output
						outputData = null
					}
				} else if (typeof selectedItem.output === 'object') {
					outputData = selectedItem.output
				}

				// 根据不同的 output 结构提取 URL
				if (outputData) {
					// 1. Veo3 / Veo3-HF 格式: { url, waterMarkedUrl, originalUrl }
					if (outputData.url && outputData.waterMarkedUrl) {
						originalUrl = outputData.url // R2 不带水印
						watermarkedUrl = outputData.waterMarkedUrl // R2 带水印
					}
					// 2. WAN22 视频格式: { video: { url, stored_url, original_url } }
					else if (outputData.video && outputData.video.url) {
						originalUrl = outputData.video.url || outputData.video.stored_url
						// WAN22 格式没有水印，使用同一个 URL
						watermarkedUrl = originalUrl
					}
					// 3. WAN22 图片格式: { image: { url, stored_url, original_url } }
					else if (outputData.image && outputData.image.url) {
						originalUrl = outputData.image.url || outputData.image.stored_url
						watermarkedUrl = originalUrl
					}
					// 4. 简单格式: { url }
					else if (outputData.url) {
						originalUrl = outputData.url
						watermarkedUrl = originalUrl
					}
					// 5. 错误格式: { error }
					else if (outputData.error) {
						console.warn('Output contains error:', outputData.error)
					}
				}
			}

			// 如果没有解析到URL，使用mediaUrl作为fallback
			if (!originalUrl && !watermarkedUrl) {
				originalUrl = mediaUrl
				watermarkedUrl = mediaUrl
			}
			
			// 判断用户是否是付费用户
			const isPaidUser = Boolean(currentUser?.has_access || isPaid)
			
			console.log('🔍 权限计算调试:', {
				'currentUser?.has_access': currentUser?.has_access,
				'isPaid': isPaid,
				'selectedItem.is_paid': selectedItem?.is_paid,
				'Boolean(currentUser?.has_access)': Boolean(currentUser?.has_access),
				'Boolean(isPaid)': Boolean(isPaid),
				'isPaidUser': isPaidUser
			})
			
			// 根据付费状态选择显示的URL
			let displayUrl = mediaUrl // fallback
			if (originalUrl && watermarkedUrl) {
				displayUrl = isPaidUser ? originalUrl : watermarkedUrl
			} else if (originalUrl) {
				displayUrl = originalUrl
			}
			
			// 下载URL也根据付费状态选择
			let downloadUrl = mediaUrl // fallback
			if (originalUrl && watermarkedUrl) {
				downloadUrl = isPaidUser ? originalUrl : watermarkedUrl
			} else if (originalUrl) {
				downloadUrl = originalUrl
			}
			
			return { originalUrl, watermarkedUrl, displayUrl, downloadUrl, isPaidUser }
		}

		const { originalUrl, watermarkedUrl, displayUrl, downloadUrl, isPaidUser } = parseVideoUrls()

		// 调试日志
		console.log('🔍 MediaDetailsModal Debug - URL解析结果:', {
			videoType: selectedItem.originalType || selectedItem.type,
			isImage,
			originalUrl: originalUrl, // url字段，不带水印
			watermarkedUrl: watermarkedUrl, // waterMarkedUrl字段，带水印
			displayUrl,
			downloadUrl,
			isPaidUser,
			hasAccess: currentUser?.has_access,
			isPaid,
			shouldShowDownloadOptions: !isImage && !isPaidUser,
			selectedItemOutput: selectedItem.output
		})



		// 主下载函数 - 使用统一的下载选项组件
		const handleDownload = () => {
			console.log('🚀 按钮被点击了！')
			console.log('🔧 handleDownload 调试信息:', {
				type: isImage ? 'image' : 'video',
				url: originalUrl || displayUrl,
				hasAccess: isPaidUser,
				watermarkUrl: watermarkedUrl || displayUrl,
				originalUrl,
				displayUrl,
				watermarkedUrl,
				isPaidUser,
				downloadOptions
			})
			
			try {
				downloadOptions.download({
					type: isImage ? 'image' : 'video',
					url: originalUrl || displayUrl,
					hasAccess: isPaidUser,
					watermarkUrl: watermarkedUrl || displayUrl,
				})
				console.log('✅ downloadOptions.download 调用成功')
			} catch (error) {
				console.error('❌ downloadOptions.download 调用失败:', error)
			}
		}

		// 处理图片点击放大
		const handleImageClick = (imageUrl) => {
			setZoomImage(imageUrl)
		}

		// 关闭放大查看
		const closeZoom = () => {
			setZoomImage(null)
		}

		// 创建相似内容
		const createSimilar = () => {
			let targetUrl = '';
			const encodedPrompt = encodeURIComponent(prompt);

			// 判断跳转到哪个页面
			if (isImage) {
				// 图像相关页面
				if (userFriendlyData?.has_input_image) {
					// 图生图
					targetUrl = `/image-to-image?prompt=${encodedPrompt}`;
				} else {
					// 文本生图
					targetUrl = `/ai-image-generator?prompt=${encodedPrompt}`;
				}
			} else {
				// 视频相关页面 - 统一跳转到 /veo3
				targetUrl = `/veo3?prompt=${encodedPrompt}`;
			}

			// 关闭对话框并跳转
			onClose();
			router.push(targetUrl);
		}

		// 跳转到图生视频页面
		const handleImageToVideo = () => {
			// 关闭对话框
			onClose();
			// 跳转到图生视频页面，并带上图片URL和prompt
			const imageUrl = isImage ? displayUrl : mediaUrl;
			router.push(`/image-to-video?imageUrl=${encodeURIComponent(imageUrl)}&prompt=${encodeURIComponent(prompt)}`);
		}

		// 分享
		const handleShare = () => {
			// 关闭对话框
			onClose();

			router.push(`/share?params=${btoa(JSON.stringify(details))}`);
		}

		return (
			<>
				{createPortal(
					<AnimatePresence>
				<motion.div
					className={`fixed inset-0 z-[10000] bg-black/80 backdrop-blur-md flex items-center justify-center ${
						isMobile ? 'p-2' : 'p-4'
					}`}
					initial={{ opacity: 0 }}
					animate={{ opacity: 1 }}
					exit={{ opacity: 0 }}
					onClick={onClose}
				>
					<motion.div
						className={`bg-black/90 border border-green-500/30 rounded-xl overflow-hidden w-full shadow-[0_0_30px_rgba(37,99,235,0.15)] ${
							isMobile 
								? 'max-w-[95vw] max-h-[95vh] mx-2' 
								: 'max-w-4xl max-h-[90vh]'
						}`}
						initial={{ scale: 0.9, y: 20 }}
						animate={{ scale: 1, y: 0 }}
						exit={{ scale: 0.9, y: 20 }}
						onClick={e => e.stopPropagation()}
					>
						<div className={`flex justify-between items-center border-b border-green-500/20 ${
							isMobile ? 'p-3' : 'p-4'
						}`}>
							<div className={`flex items-center ${isMobile ? 'space-x-1' : 'space-x-2'}`}>
								<h3 className={`font-bold bg-gradient-to-r from-green-400 to-cyan-400 bg-clip-text text-transparent ${
									isMobile ? 'text-lg' : 'text-xl'
								}`}>
									{isImage ? 'Image Details' : 'Video Details'}
								</h3>
								{hasFullAccess && (
									<div className={`px-2 py-0.5 rounded-full text-xs bg-green-500/20 text-green-400 flex items-center ${
										isMobile ? 'hidden' : ''
									}`}>
										<Shield className="w-3 h-3 mr-1" />
										Full Access
									</div>
								)}
								{!isImage && watermarkedUrl && originalUrl && (
									<div className={`px-2 py-0.5 rounded-full text-xs flex items-center ${
										isPaidUser 
											? 'bg-green-500/20 text-green-400' 
											: 'bg-amber-500/20 text-amber-400'
									} ${isMobile ? 'hidden' : ''}`}>
										{isPaidUser ? (
											<>
												<Shield className="w-3 h-3 mr-1" />
												No Watermark
											</>
										) : (
											<>
												<Eye className="w-3 h-3 mr-1" />
												Watermarked
											</>
										)}
									</div>
								)}
							</div>
							<button
								className={`rounded-full bg-black/40 hover:bg-black/60 border border-green-500/30 transition-colors ${
									isMobile ? 'p-1' : 'p-1.5'
								}`}
								onClick={onClose}
							>
								<X className={`text-gray-300 ${isMobile ? 'w-5 h-5' : 'w-4 h-4'}`} />
							</button>
						</div>

						<div className={`${
							isMobile 
								? 'flex flex-col gap-3 p-3 overflow-y-auto max-h-[calc(95vh-60px)]' 
								: 'grid grid-cols-1 md:grid-cols-2 gap-4 p-4 overflow-y-auto max-h-[calc(90vh-60px)]'
						}`}>
							{/* 媒体展示 */}
							{userFriendlyData?.has_input_image ? (
								// 如果是图生图或图生视频，显示输入/输出对比
								<div className="flex flex-col space-y-4">
									{/* 输入和输出图片标题 */}
									<div className="flex items-center mb-1">
										<h4 className="text-sm font-medium text-green-400 flex items-center">
											<Wand2 className="w-4 h-4 mr-1.5" />
											{userFriendlyData.is_image_to_video ? 'Image to Video' : 'Image to Image'}
										</h4>
									</div>

									{/* PC端上下布局，移动端左右布局 */}
									<div className="flex flex-col space-y-4">
										{/* PC端上下布局 */}
										<div className={isMobile ? 'block' : 'hidden md:block'}>
											<div className={isMobile ? 'space-y-3' : 'space-y-4'}>
												{/* 输出图片/视频 (上) */}
												<div
													className={`relative rounded-lg overflow-hidden bg-black/50 flex items-center justify-center border border-green-500/20 ${
														isMobile ? 'aspect-[4/3]' : 'aspect-square'
													} ${isImage ? 'cursor-zoom-in group' : ''}`}
													onClick={() => isImage && mediaUrl && handleImageClick(mediaUrl)}
												>
													{isImage ? (
														mediaUrl ? (
															<>
																<Image
																	src={mediaUrl}
																	alt={prompt || 'Generated image'}
																	fill
																	className="object-contain transition-transform group-hover:scale-105"
																/>
																<div className="absolute inset-0 flex items-center justify-center bg-black/30 opacity-0 group-hover:opacity-100 transition-opacity">
																	<ZoomIn className="w-10 h-10 text-white opacity-80" />
																</div>
															</>
														) : (
															<div className="text-gray-400 text-sm">Image URL not available</div>
														)
													) : (
														displayUrl ? (
															<video
																src={displayUrl}
																controls
																className="max-h-full max-w-full"
																autoPlay
																loop
																controlsList="nodownload"
															/>
														) : (
															<div className="text-gray-400 text-sm">Video URL not available</div>
														)
													)}
													<div className="absolute top-2 left-2 px-2 py-0.5 rounded-md bg-black/70 text-white text-xs border border-green-500/30">
														Output
													</div>
												</div>

												{/* 箭头指示转换 (中) */}
												<div className="relative flex justify-center items-center py-1">
													<div className="w-8 h-8 rounded-full bg-gradient-to-r from-green-500 to-cyan-500 flex items-center justify-center transform -rotate-90">
														<svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="text-white">
															<path d="M5 12h14"></path>
															<path d="m12 5 7 7-7 7"></path>
														</svg>
													</div>
												</div>

												{/* 输入图片 (下) */}
												{userFriendlyData.input_image_url && (
													<div
														className={`relative rounded-lg overflow-hidden bg-black/60 flex items-center justify-center border border-green-500/20 cursor-zoom-in group ${
															isMobile ? 'aspect-[4/3]' : 'aspect-square'
														}`}
														onClick={() => handleImageClick(userFriendlyData.input_image_url)}
													>
														<Image
															src={userFriendlyData.input_image_url}
															alt="Input image"
															fill
															className="object-contain transition-transform group-hover:scale-105"
														/>
														<div className="absolute top-2 left-2 px-2 py-0.5 rounded-md bg-black/70 text-white text-xs border border-green-500/30">
															Input
														</div>
														<div className="absolute inset-0 flex items-center justify-center bg-black/30 opacity-0 group-hover:opacity-100 transition-opacity">
															<ZoomIn className="w-10 h-10 text-white opacity-80" />
														</div>
													</div>
												)}
											</div>
										</div>

										{/* 移动端左右布局 - 在新的布局中已不需要 */}
										<div className="hidden">
											{/* 输入图片 */}
											{userFriendlyData.input_image_url && (
												<div
													className="relative rounded-lg overflow-hidden aspect-square bg-black/60 flex items-center justify-center border border-green-500/20 cursor-zoom-in group"
													onClick={() => handleImageClick(userFriendlyData.input_image_url)}
												>
													<Image
														src={userFriendlyData.input_image_url}
														alt="Input image"
														fill
														className="object-contain transition-transform group-hover:scale-105"
													/>
													<div className="absolute top-2 left-2 px-2 py-0.5 rounded-md bg-black/70 text-white text-xs border border-green-500/30">
														Input
													</div>
													<div className="absolute inset-0 flex items-center justify-center bg-black/30 opacity-0 group-hover:opacity-100 transition-opacity">
														<ZoomIn className="w-10 h-10 text-white opacity-80" />
													</div>
												</div>
											)}

											{/* 输出图片/视频 */}
											<div
												className={`relative rounded-lg overflow-hidden aspect-square bg-black/50 flex items-center justify-center border border-green-500/20 ${isImage ? 'cursor-zoom-in group' : ''}`}
												onClick={() => isImage && mediaUrl && handleImageClick(mediaUrl)}
											>
												{isImage ? (
													mediaUrl ? (
														<>
															<Image
																src={mediaUrl}
																alt={prompt || 'Generated image'}
																fill
																className="object-contain transition-transform group-hover:scale-105"
															/>
															<div className="absolute inset-0 flex items-center justify-center bg-black/30 opacity-0 group-hover:opacity-100 transition-opacity">
																<ZoomIn className="w-10 h-10 text-white opacity-80" />
															</div>
														</>
													) : (
														<div className="text-gray-400 text-sm">Image URL not available</div>
													)
												) : (
													displayUrl ? (
														<video
															src={displayUrl}
															controls
															className="max-h-full max-w-full"
															autoPlay
															loop
															controlsList="nodownload"
														/>
													) : (
														<div className="text-gray-400 text-sm">Video URL not available</div>
													)
												)}
												<div className="absolute top-2 left-2 px-2 py-0.5 rounded-md bg-black/70 text-white text-xs border border-green-500/30">
													Output
												</div>
											</div>
										</div>

										{/* 移动端的箭头指示 */}
										<div className="relative md:hidden">
											<div className="absolute left-1/2 top-[-24px] transform -translate-x-1/2 w-8 h-8 rounded-full bg-gradient-to-r from-green-500 to-cyan-500 flex items-center justify-center">
												<svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="text-white">
													<path d="M5 12h14"></path>
													<path d="m12 5 7 7-7 7"></path>
												</svg>
											</div>
										</div>
									</div>
								</div>
							) : (
								// 如果不是图生图/图生视频，显示普通的媒体
								<div
									className={`relative rounded-lg overflow-hidden bg-black/50 flex items-center justify-center border border-green-500/20 group ${
										isMobile ? 'aspect-[4/3]' : 'aspect-square'
									}`}
									onClick={() => isImage && mediaUrl && handleImageClick(mediaUrl)}
								>
									{isImage ? (
										mediaUrl ? (
											<>
												<Image
													src={mediaUrl}
													alt={prompt || 'Generated image'}
													fill
													className="object-contain transition-transform group-hover:scale-105"
												/>
												<div className="absolute inset-0 flex items-center justify-center bg-black/30 opacity-0 group-hover:opacity-100 transition-opacity cursor-zoom-in">
													<ZoomIn className="w-12 h-12 text-white opacity-80" />
												</div>
											</>
										) : (
											<div className="text-gray-400 text-sm">Image URL not available</div>
										)
									) : (
										displayUrl ? (
											<video
												src={displayUrl}
												controls
												className="max-h-full max-w-full"
												autoPlay
												loop
												controlsList="nodownload"
											/>
										) : (
											<div className="text-gray-400 text-sm">Video URL not available</div>
										)
									)}
								</div>
							)}

							{/* 详情信息 */}
							<div className="flex flex-col">
								<div className={isMobile ? 'space-y-3' : 'space-y-4'}>
									<div>
										<h4 className={`font-medium text-gray-400 mb-1 ${
											isMobile ? 'text-xs' : 'text-sm'
										}`}>Prompt</h4>
										<p className={`text-white/90 border border-green-500/20 rounded-lg bg-black/40 ${
											isMobile ? 'text-xs p-2' : 'text-sm p-3'
										}`}>
											{prompt}
										</p>
									</div>

									<div className={isMobile ? 'grid grid-cols-1 gap-2' : 'grid grid-cols-2 gap-4'}>
										<div>
											<h4 className={`font-medium text-gray-400 mb-1 ${
												isMobile ? 'text-xs' : 'text-sm'
											}`}>Type</h4>
											<p className={`text-white/90 flex items-center ${
												isMobile ? 'text-xs' : 'text-sm'
											}`}>
												{isImage ? (
													<>
														<ImageIcon className={`mr-2 text-green-400 ${
															isMobile ? 'w-3 h-3' : 'w-4 h-4'
														}`} />
														Image
													</>
												) : (
													<>
														<Film className={`mr-2 text-cyan-400 ${
															isMobile ? 'w-3 h-3' : 'w-4 h-4'
														}`} />
														Video
													</>
												)}
											</p>
										</div>
										<div>
											<h4 className={`font-medium text-gray-400 mb-1 ${
												isMobile ? 'text-xs' : 'text-sm'
											}`}>Created</h4>
											<p className={`text-white/90 flex items-center ${
												isMobile ? 'text-xs' : 'text-sm'
											}`}>
												<Clock className={`mr-2 text-gray-500 ${
													isMobile ? 'w-3 h-3' : 'w-4 h-4'
												}`} />
												{timestamp}
											</p>
										</div>
									</div>

									<div className={isMobile ? 'grid grid-cols-1 gap-2' : 'grid grid-cols-2 gap-4'}>
										<div>
											<h4 className={`font-medium text-gray-400 mb-1 ${
												isMobile ? 'text-xs' : 'text-sm'
											}`}>Status</h4>
											<div className={`inline-flex items-center rounded-full ${
												isMobile ? 'px-2 py-0.5 text-xs' : 'px-2.5 py-1 text-xs'
											} ${status === 2 ? 'bg-green-500/20 text-green-400' :
													status === 1 ? 'bg-amber-500/20 text-amber-400' :
														'bg-red-500/20 text-red-400'}`}>
												{status === 2 ? 'Completed' :
													status === 1 ? 'Processing' :
														'Failed'}
											</div>
										</div>

										{model && (
											<div>
												<h4 className={`font-medium text-gray-400 mb-1 ${
													isMobile ? 'text-xs' : 'text-sm'
												}`}>Model</h4>
												<p className={`text-white/90 flex items-center ${
													isMobile ? 'text-xs' : 'text-sm'
												}`}>
													<Tag className={`mr-2 text-gray-500 ${
														isMobile ? 'w-3 h-3' : 'w-4 h-4'
													}`} />
													{model || userFriendlyData?.model || 'Unknown'}
												</p>
											</div>
										)}
									</div>

									{/* 用户友好的数据展示 */}
									{userFriendlyData && Object.keys(userFriendlyData).length > 0 && (
										<div className="pt-2 border-t border-green-500/20">
											<h4 className="text-sm font-medium text-green-400 mb-3 flex items-center">
												<Settings className="w-4 h-4 mr-1.5" />
												Generation Settings
											</h4>

											<div className="grid grid-cols-2 gap-x-4 gap-y-2">
												{isImage ? (
													// 图片特有的参数
													<>
														{/* 尺寸信息 */}
														{userFriendlyData.width && userFriendlyData.height && (
															<div>
																<h4 className="text-xs font-medium text-gray-500 mb-0.5">Dimensions</h4>
																<p className="text-white/90 text-xs flex items-center">
																	<Maximize2 className="w-3 h-3 mr-1 text-gray-500" />
																	{userFriendlyData.width} × {userFriendlyData.height}
																</p>
															</div>
														)}

														{/* 长宽比 */}
														{userFriendlyData.aspect_ratio && (
															<div>
																<h4 className="text-xs font-medium text-gray-500 mb-0.5">Aspect Ratio</h4>
																<p className="text-white/90 text-xs flex items-center">
																	<Maximize className="w-3 h-3 mr-1 text-gray-500" />
																	{userFriendlyData.aspect_ratio}
																</p>
															</div>
														)}

														{/* 步数 */}
														{userFriendlyData.steps && (
															<div>
																<h4 className="text-xs font-medium text-gray-500 mb-0.5">Steps</h4>
																<p className="text-white/90 text-xs flex items-center">
																	<Layers className="w-3 h-3 mr-1 text-gray-500" />
																	{userFriendlyData.steps}
																</p>
															</div>
														)}

														{/* 引导规模 */}
														{userFriendlyData.guidance_scale && (
															<div>
																<h4 className="text-xs font-medium text-gray-500 mb-0.5">Guidance Scale</h4>
																<p className="text-white/90 text-xs flex items-center">
																	<Scale className="w-3 h-3 mr-1 text-gray-500" />
																	{userFriendlyData.guidance_scale}
																</p>
															</div>
														)}

														{/* 输出格式 */}
														{userFriendlyData.output_format && (
															<div>
																<h4 className="text-xs font-medium text-gray-500 mb-0.5">Format</h4>
																<p className="text-white/90 text-xs flex items-center">
																	<FileType className="w-3 h-3 mr-1 text-gray-500" />
																	{userFriendlyData.output_format.toUpperCase()}
																</p>
															</div>
														)}

														{/* 输出质量 */}
														{userFriendlyData.output_quality && (
															<div>
																<h4 className="text-xs font-medium text-gray-500 mb-0.5">Quality</h4>
																<p className="text-white/90 text-xs flex items-center">
																	<Percent className="w-3 h-3 mr-1 text-gray-500" />
																	{userFriendlyData.output_quality}%
																</p>
															</div>
														)}

														{/* 安全检查 */}
														{userFriendlyData.safety_checker !== undefined && (
															<div>
																<h4 className="text-xs font-medium text-gray-500 mb-0.5">Safety Checker</h4>
																<p className="text-white/90 text-xs flex items-center">
																	<Eye className="w-3 h-3 mr-1 text-gray-500" />
																	{userFriendlyData.safety_checker ? 'Enabled' : 'Disabled'}
																</p>
															</div>
														)}

														{/* 图生图参数 */}
														{userFriendlyData.has_input_image && (
															<>
																<div>
																	<h4 className="text-xs font-medium text-gray-500 mb-0.5">Mode</h4>
																	<p className="text-white/90 text-xs flex items-center">
																		<Wand2 className="w-3 h-3 mr-1 text-green-400" />
																		Image to Image
																	</p>
																</div>

																{/* 提示词强度 */}
																{userFriendlyData.prompt_strength && (
																	<div>
																		<h4 className="text-xs font-medium text-gray-500 mb-0.5">Prompt Strength</h4>
																		<p className="text-white/90 text-xs flex items-center">
																			<Scale className="w-3 h-3 mr-1 text-gray-500" />
																			{userFriendlyData.prompt_strength}
																		</p>
																	</div>
																)}

																{/* 图像提示强度 */}
																{userFriendlyData.image_prompt_strength && (
																	<div>
																		<h4 className="text-xs font-medium text-gray-500 mb-0.5">Image Strength</h4>
																		<p className="text-white/90 text-xs flex items-center">
																			<Scale className="w-3 h-3 mr-1 text-gray-500" />
																			{userFriendlyData.image_prompt_strength}
																		</p>
																	</div>
																)}
															</>
														)}
													</>
												) : (
													// 视频特有的参数
													<>
														{/* 时长 */}
														{userFriendlyData.duration && (
															<div>
																<h4 className="text-xs font-medium text-gray-500 mb-0.5">Duration</h4>
																<p className="text-white/90 text-xs flex items-center">
																	<Clock className="w-3 h-3 mr-1 text-gray-500" />
																	{userFriendlyData.duration}s
																</p>
															</div>
														)}

														{/* 纵横比 */}
														{userFriendlyData.aspect_ratio && (
															<div>
																<h4 className="text-xs font-medium text-gray-500 mb-0.5">Aspect Ratio</h4>
																<p className="text-white/90 text-xs flex items-center">
																	<Maximize className="w-3 h-3 mr-1 text-gray-500" />
																	{userFriendlyData.aspect_ratio}
																</p>
															</div>
														)}

														{/* 质量 */}
														{userFriendlyData.quality && (
															<div>
																<h4 className="text-xs font-medium text-gray-500 mb-0.5">Quality</h4>
																<p className="text-white/90 text-xs flex items-center">
																	<Settings className="w-3 h-3 mr-1 text-gray-500" />
																	{userFriendlyData.quality}
																</p>
															</div>
														)}

														{/* 动态模式 */}
														{userFriendlyData.motion_mode && (
															<div>
																<h4 className="text-xs font-medium text-gray-500 mb-0.5">Motion</h4>
																<p className="text-white/90 text-xs flex items-center">
																	<Film className="w-3 h-3 mr-1 text-gray-500" />
																	{userFriendlyData.motion_mode}
																</p>
															</div>
														)}

														{/* 图生视频模式 */}
														{userFriendlyData.is_image_to_video && (
															<div>
																<h4 className="text-xs font-medium text-gray-500 mb-0.5">Mode</h4>
																<p className="text-white/90 text-xs flex items-center">
																	<Wand2 className="w-3 h-3 mr-1 text-green-400" />
																	Image to Video
																</p>
															</div>
														)}

														{/* FPS */}
														{userFriendlyData.fps && (
															<div>
																<h4 className="text-xs font-medium text-gray-500 mb-0.5">FPS</h4>
																<p className="text-white/90 text-xs flex items-center">
																	<Film className="w-3 h-3 mr-1 text-gray-500" />
																	{userFriendlyData.fps}
																</p>
															</div>
														)}

														{/* 模板ID */}
														{userFriendlyData.template_id && (
															<div>
																<h4 className="text-xs font-medium text-gray-500 mb-0.5">Template</h4>
																<p className="text-white/90 text-xs flex items-center">
																	<LayoutTemplate className="w-3 h-3 mr-1 text-gray-500" />
																	{userFriendlyData.template_id}
																</p>
															</div>
														)}

														{/* 音频 */}
														{userFriendlyData.has_audio && (
															<div>
																<h4 className="text-xs font-medium text-gray-500 mb-0.5">Audio</h4>
																<p className="text-white/90 text-xs flex items-center">
																	<Music className="w-3 h-3 mr-1 text-gray-500" />
																	Yes
																</p>
															</div>
														)}

														{/* Webhook */}
														{userFriendlyData.webhook !== undefined && (
															<div>
																<h4 className="text-xs font-medium text-gray-500 mb-0.5">Webhook</h4>
																<p className="text-white/90 text-xs flex items-center">
																	<Chrome className="w-3 h-3 mr-1 text-gray-500" />
																	{userFriendlyData.webhook ? 'Enabled' : 'Disabled'}
																</p>
															</div>
														)}
													</>
												)}

												{/* 通用参数 */}
												{userFriendlyData.seed && (
													<div>
														<h4 className="text-xs font-medium text-gray-500 mb-0.5">Seed</h4>
														<p className="text-white/90 text-xs flex items-center">
															<Hash className="w-3 h-3 mr-1 text-gray-500" />
															{userFriendlyData.seed || 'Random'}
														</p>
													</div>
												)}

												{userFriendlyData.negative_prompt && (
													<div className="col-span-2 mt-1">
														<h4 className="text-xs font-medium text-gray-500 mb-0.5">Negative Prompt</h4>
														<p className="text-white/90 text-xs border border-green-500/20 rounded-md p-1.5 bg-black/40 line-clamp-2">
															{userFriendlyData.negative_prompt || 'None'}
														</p>
													</div>
												)}
											</div>
										</div>
									)}

									{/* 展示公开信息 - Explore特有 */}
									{source === 'explore' && hasFullAccess && (
										<div className="grid grid-cols-2 gap-4 pt-2 border-t border-green-500/20">
											<div>
												<h4 className="text-sm font-medium text-gray-400 mb-1">Featured</h4>
												<div className={`
                          px-2.5 py-1 rounded-full text-xs inline-flex items-center
                          ${isFeatured ? 'bg-green-500/20 text-green-400' : 'bg-gray-500/20 text-gray-400'}
                        `}>
													<Award className="w-3 h-3 mr-1" />
													{isFeatured ? 'Yes' : 'No'}
												</div>
											</div>
											<div>
												<h4 className="text-sm font-medium text-gray-400 mb-1">Premium</h4>
												<div className={`
                          px-2.5 py-1 rounded-full text-xs inline-flex items-center
                          ${isPaid ? 'bg-amber-500/20 text-amber-400' : 'bg-gray-500/20 text-gray-400'}
                        `}>
													<DollarSign className="w-3 h-3 mr-1" />
													{isPaid ? 'Yes' : 'No'}
												</div>
											</div>
										</div>
									)}

									{/* 权限内容 - 只有管理员或内容创建者可见 */}
									{hasFullAccess && (
										<>
																			<div className="pt-2 border-t border-green-500/20">
									<h4 className="text-sm font-medium text-green-400 mb-3 flex items-center">
													<Info className="w-4 h-4 mr-1.5" />
													Admin Details
												</h4>

												<div className="grid grid-cols-2 gap-4">
													<div>
														<h4 className="text-sm font-medium text-gray-400 mb-1">Task ID</h4>
														<p className="text-white/90 text-xs truncate">
															{taskId}
														</p>
													</div>

													{id && (
														<div>
															<h4 className="text-sm font-medium text-gray-400 mb-1">ID</h4>
															<p className="text-white/90 text-xs truncate">
																{id}
															</p>
														</div>
													)}
												</div>

												{email && (
													<div className="mt-3">
														<h4 className="text-sm font-medium text-gray-400 mb-1">Creator</h4>
														<p className="text-white/90 text-sm flex items-center">
															<User className="w-4 h-4 mr-2 text-gray-500" />
															{email}
														</p>
													</div>
												)}

												{userId && (
													<div className="mt-3">
														<h4 className="text-sm font-medium text-gray-400 mb-1">User ID</h4>
														<p className="text-white/90 text-xs truncate">
															{userId}
														</p>
													</div>
												)}

												{rawData && (
													<div className="mt-3">
														<h4 className="text-sm font-medium text-gray-400 mb-1 flex items-center">
															<Database className="w-3.5 h-3.5 mr-1.5" />
															Raw Data
														</h4>
														<div className="mt-1 relative">
															<div className="absolute inset-0 bg-gradient-to-b from-transparent via-transparent to-black/90 z-10 pointer-events-none"></div>
															<pre className="text-xs text-gray-400 bg-black/30 p-2 rounded-md border border-green-500/20 overflow-auto max-h-24 font-mono">
																{typeof rawData === 'string' ? rawData : JSON.stringify(rawData, null, 2)}
															</pre>
														</div>
													</div>
												)}
											</div>
										</>
									)}

									{/* 下载按钮 */}
									{status === 2 && (
										<div className={`${isMobile ? 'mt-3 space-y-2' : 'mt-4 space-y-2'}`}>
											<button
												onClick={(e) => {
													e.preventDefault()
													e.stopPropagation()
													console.log('🎯 按钮直接点击事件触发')
													handleDownload()
												}}
												className={`${DOWNLOAD_BUTTON} ${
													isMobile ? 'w-full py-3 text-sm' : ''
												}`}
												style={{ pointerEvents: 'auto', cursor: 'pointer' }}
											>
												{isImage ? 'Download Image' : 'Download Video'}
											</button>

											{/* 创建相似内容按钮 */}
											{/* <button
												onClick={createSimilar}
												className={`${CREATE_SIMILAR_BUTTON} ${
													isMobile ? 'w-full py-3 text-sm' : ''
												}`}
											>
												<Copy className={`text-white mr-2 ${
													isMobile ? 'w-4 h-4' : 'w-4 h-4'
												}`} />
												Create Similar {isImage ? 'Image' : 'Video'}
											</button> */}

											{isImage && (
												<button
													onClick={handleImageToVideo}
													className={`${CREATE_SIMILAR_BUTTON} ${
														isMobile ? 'w-full py-3 text-sm' : ''
													}`}
												>
													<Video className={`text-white mr-2 ${
														isMobile ? 'w-4 h-4' : 'w-4 h-4'
													}`} />
													Image to Video
												</button>
											)}

											<button
												onClick={handleShare}
												className={`${CREATE_SIMILAR_BUTTON} ${
													isMobile ? 'w-full py-3 text-sm' : ''
												}`}
											>
												<Share className={`text-white mr-2 ${
													isMobile ? 'w-4 h-4' : 'w-4 h-4'
												}`} />
												Share
											</button>
										</div>
									)}
								</div>
							</div>
						</div>
					</motion.div>

					{/* 图片放大查看模态框 */}
					<AnimatePresence>
						{zoomImage && (
							<motion.div
								className={`fixed inset-0 z-[10001] bg-black/95 backdrop-blur-lg flex items-center justify-center ${
									isMobile ? 'p-2' : 'p-4'
								}`}
								initial={{ opacity: 0 }}
								animate={{ opacity: 1 }}
								exit={{ opacity: 0 }}
								onClick={closeZoom}
							>
								<motion.div
									className={`relative overflow-hidden flex items-center justify-center ${
										isMobile ? 'max-w-[95vw] max-h-[90vh]' : 'max-w-[90vw] max-h-[90vh]'
									}`}
									initial={{ scale: 0.9 }}
									animate={{ scale: 1 }}
									exit={{ scale: 0.9 }}
								>
									<Image
										src={zoomImage}
										alt="Zoomed image"
										width={1200}
										height={1200}
										className={`object-contain rounded-lg shadow-[0_0_30px_rgba(168,85,247,0.3)] ${
											isMobile ? 'max-w-full max-h-[90vh]' : 'max-w-full max-h-[90vh]'
										}`}
									/>
									<button
										className={`absolute bg-black/70 hover:bg-black/90 border border-green-500/30 transition-colors rounded-full ${
											isMobile ? 'top-2 right-2 p-2' : 'top-4 right-4 p-2'
										}`}
										onClick={(e) => {
											e.stopPropagation();
											closeZoom();
										}}
									>
										<X className={`text-white ${isMobile ? 'w-5 h-5' : 'w-6 h-6'}`} />
									</button>
								</motion.div>
							</motion.div>
						)}
					</AnimatePresence>


				</motion.div>
			</AnimatePresence>,
			document.body
				)}
				
				{/* DownloadOptions 也使用 createPortal 独立渲染 */}
				{createPortal(
					<DownloadOptions {...downloadOptions} />,
					document.body
				)}
			</>
		)
	} catch (error) {
		console.error('Error rendering details modal:', error)
		toast.error('Failed to display details')
		return null
	}
} 